import {
  Component,
  OnInit,
  OnDestroy,
  input,
  inject,
  computed,
  linkedSignal,
  signal,
  ElementRef,
  ViewChild,
  effect
} from '@angular/core';
import { CommonModule } from '@angular/common';
import BaseLayer from 'ol/layer/Base';
import LayerGroup from 'ol/layer/Group';
import { animate } from 'animejs';
import { fromEvent, Subscription,combineLatest, merge } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { collectionEvents$, collectionRemovals$ } from './helpers';
import { TruncateTextPipe } from 'src/app/pipes/truncate-text.pipe';
import { TooltipDirective } from 'src/app/components/shared/directives/tooltip/tooltip.directive';

@Component({
  selector: 'app-base-float',
  templateUrl: './base-float.component.html',
  styleUrls: ['./base-float.component.css'],
  standalone: true,
  imports: [CommonModule,TruncateTextPipe,TooltipDirective],
})
export class BaseFloatComponent implements OnInit, OnDestroy {
  @ViewChild('gallery', { static: false }) gallery!: ElementRef;

  readonly layerGroup = input<LayerGroup>(undefined);


  private subscription = new Subscription();

  readonly layers = signal<BaseLayer[]>([]);

  selectedBaseMap = linkedSignal<BaseLayer | null>(() => {
    return this.layers().find((layer) => layer.getVisible()) ?? null;
  });

  isOpen = signal(false);

  constructor() {
    // Use effect to reactively watch for layerGroup changes
    effect(() => {
      const layerGroup = this.layerGroup();
      console.log("Effect running - layerGroup:", layerGroup);

      if (layerGroup == null) {
        console.log("LayerGroup is null, skipping setup");
        return;
      }

      console.log("Setting up layer group listeners for:", layerGroup);

      // Clear any existing subscriptions
      this.subscription.unsubscribe();
      this.subscription = new Subscription();

      // Initial load of layers - filter out null/undefined layers
      const initialLayers = layerGroup.getLayersArray().filter(layer => layer != null);
      console.log("Initial BaseLayers", initialLayers);
      this.layers.set(initialLayers);

      // Set up event listeners for layer changes
      const added$ = collectionEvents$(layerGroup);
      const removed$ = collectionRemovals$(layerGroup);
      const events$ = merge(added$, removed$);

      // Subscribe to layer changes
      this.subscription.add(
        events$.subscribe(() => {
          const layers = layerGroup.getLayersArray().filter(layer => layer != null);
          console.log("Updated BaseLayers", layers);
          this.layers.set(layers);
        })
      );
    });
  }

  openGallery() {
    const isCurrentlyOpen = this.isOpen();
    this.isOpen.set(!isCurrentlyOpen);

    // Refresh layers when opening gallery as a fallback
    if (!isCurrentlyOpen) {
      this.refreshLayers();
    }

    if (this.gallery) {
      if (!isCurrentlyOpen) {
        // Opening animation
        this.gallery.nativeElement.style.display = 'block';
        animate(this.gallery.nativeElement, {
          opacity: [0, 1],
          scale: [0.8, 1],
          translateY: [20, 0],
          duration: 300,
          ease: 'outCubic'
        });
      } else {
        // Closing animation
        animate(this.gallery.nativeElement, {
          opacity: [1, 0],
          scale: [1, 0.8],
          translateY: [0, 20],
          duration: 200,
          ease: 'inCubic',
          onComplete: () => {
            this.gallery.nativeElement.style.display = 'none';
          }
        });
      }
    }
  }

  getLayerImageName(layer) {
    if (layer == null) {
      return '';
    }

    // Try both 'title' and 'display_name' properties
    let title = layer.get('title') || layer.get('display_name');

    if (!title) {
      return '';
    }

    return title.replaceAll(' ', '_');
  }


  ngOnInit(): void {
    console.log("BaseFloatComponent ngOnInit - layerGroup:", this.layerGroup());
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  // Fallback method to manually refresh layers
  refreshLayers(): void {
    const layerGroup = this.layerGroup();
    if (layerGroup) {
      const layers = layerGroup.getLayersArray().filter(layer => layer != null);
      console.log("Manual refresh - BaseLayers", layers);
      this.layers.set(layers);
    }
  }



  getLayerGroupOpacityLabel(): string {
    return this.layerGroup().getOpacity() * 100 + '%';
  }

  getLayerGroupOpacity(): number {
    return this.layerGroup().getOpacity() * 100;
  }

  setLayerGroupOpacity(value: number): void {
    this.layerGroup().setOpacity(value / 100);
  }

  getLayerGroupVisibility(): boolean {
    return this.layerGroup().getVisible();
  }

  setLayerGroupVisibility(visible: boolean): void {
    this.layerGroup().setVisible(visible);
  }

  formatLabel(value: number): string {
    return value + '%';
  }

  setBaseMapVisible(newBaseMap: BaseLayer) {
    const selectedBaseMap = this.selectedBaseMap();

    if (selectedBaseMap != null) {
      selectedBaseMap.setVisible(false);
    }

    newBaseMap.setVisible(true);

    this.selectedBaseMap.set(newBaseMap);
  }

  lostFocus() {
  // Small delay to allow clicks on gallery items to register first
  setTimeout(() => {
    this.isOpen.set(false);
  }, 150);
}
}
